import { ClickAwayListener, Popper } from "@mui/base";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useLocation, useParams } from "react-router-dom";

import { apiClient } from "~/api/ApiClient";
import { Icon } from "~/assets/icons/Icon";
import ThemeToggleCompact from "~/components/settings/ThemeToggleCompact";
import { useTeam } from "~/contexts/TeamProvider";
import { FEATURE_FLAGS, useFeatureFlagsConfig } from "~/featureFlags";
import { useGoogleDashboard } from "~/hooks/useGoogleDashboard";
import { useSidebar } from "~/hooks/useSidebar";
import { routes } from "~/router";
import { isWhiteLabel } from "~/utils/white-label";

import { useSiteContext } from "../../contexts/SiteContext";
import Button from "../ui/button/Button";
import style from "./DashboardSidebar.module.scss";
import Menu, { MenuGroup, MenuItem } from "./Menu";
import Optimization from "./optimization/Optimization";
import { filterMenuGroupsByFeatureFlags } from "./utils";

type DashboardSidebarProps = {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (v: boolean) => void;
};

const createMenuItem = (item: MenuItem): MenuItem => item;

const DashboardSidebar = ({
  isSidebarOpen,
  setIsSidebarOpen,
}: DashboardSidebarProps) => {
  const { id } = useParams();

  if (!id) {
    throw new Error("DashboardSidebar: missing site id in params");
  }

  const { team } = useTeam();
  const siteContext = useSiteContext();
  const site = siteContext?.site?.site;
  const isLoadingSite = siteContext?.isLoadingSite;
  const { isSidebarEmpty } = useSidebar();

  const location = useLocation();
  const activePath = location.pathname;
  const flags = useFeatureFlagsConfig();

  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setPopoverAnchorEl(null);
  };

  const { data: siteGoals, isLoading: isSiteGoalsLoading } = useQuery({
    queryKey: ["site_goals", id],
    queryFn: async () => {
      const response = await apiClient.sites.goalsDetail(Number(id));

      const rawGoals = response?.data?.site_goals;

      if (!rawGoals) return { filteredGoals: {}, goalsLabel: "0 / 0" };

      const filteredGoals = Object.fromEntries(
        Object.entries(rawGoals).filter(([, value]) => value !== null),
      );
      const completeCount = Object.values(filteredGoals).filter(
        (value) => value === "complete",
      ).length;

      const totalCount = Object.keys(filteredGoals).length;
      const goalsLabel = `${completeCount} / ${totalCount}`;

      return {
        filteredGoals,
        goalsLabel,
        completeCount,
        totalCount,
      };
    },
    enabled: !!id && !isWhiteLabel(),
  });

  const { shouldShowGoogleDash } = useGoogleDashboard({
    siteId: Number(id),
  });

  const crawlStatus = site?.crawl_status;
  const liveEditorEnabled = Boolean(
    team?.subscription?.price?.product?.live_edior,
  );
  const contentAvailable = Boolean(
    team?.ai_title_available || team?.ai_description_available,
  );
  const schemaAvailable = Boolean(team?.ai_schema_available);
  // Hide the entry when the team's subscription doesn't include
  // prerendering, OR when this site's application was reviewed and
  // rejected — there's no path forward in the rejected case so showing a
  // preview/upsell would be misleading.
  const shouldShowAiVisibilityEngine = Boolean(
    team?.subscription?.price?.product?.prerendering,
  );
  const aiVisibilityEngineAvailable =
    shouldShowAiVisibilityEngine && site?.site_speed_status !== "rejected";

  const filteredDashboardMenu: MenuGroup[] = useMemo(() => {
    if (!id) return [];

    const dashboardMenu: MenuGroup[] = [
      {
        menuTitle: "Setup",
        menuItems: [
          createMenuItem({
            label: "Analytics",
            link: routes.site.root({ id }),
            iconName: "AnalyticsIcon",
            feature: FEATURE_FLAGS.SITES_ANALYTICS,
          }),
          createMenuItem({
            label: "Pages",
            link: routes.site.pages({ id }),
            iconName: ["in_progress", "analysing", "finished"].includes(
              crawlStatus ?? "",
            )
              ? "PagesIconProcessing"
              : "PagesIcon",
            feature: FEATURE_FLAGS.SITES_PAGES,
          }),
          createMenuItem({
            label: "Site Settings",
            link: routes.site.settings.root({ id }),
            iconName: "SiteSettingsIcon",
            feature: FEATURE_FLAGS.SITES_SETTINGS_GENERAL,
          }),
          ...(shouldShowGoogleDash
            ? [
                createMenuItem({
                  label: "Google Dashboard",
                  link: routes.site.googleDashboard({ id }),
                  iconName: "DashboardIcon",
                  feature: FEATURE_FLAGS.SITES_GOOGLE_DASHBOARD,
                }),
              ]
            : []),
        ],
      },
      {
        menuTitle: "Tools",
        menuItems: [
          createMenuItem({
            label: "Visibility Engine",
            iconName: "EyeIcon",
            subMenu: [
              ...(shouldShowAiVisibilityEngine
                ? [
                    {
                      label: "Metrics",
                      link: routes.site.visibilityMetrics({ id }),
                      feature: FEATURE_FLAGS.SITES_VISIBILITY_ENGINE,
                    },
                    {
                      label: "Settings",
                      link: routes.site.visibilityEngineSettings({ id }),
                      feature: FEATURE_FLAGS.SITES_SETTINGS_PRERENDERING,
                    },
                    {
                      label: "Configuration",
                      link: site?.delegated_domain?.id
                        ? routes.site.visibilityEngine.domain({
                            id,
                            domainId: site.delegated_domain.id,
                          })
                        : routes.site.visibilityEngine.about({ id }),
                      feature: FEATURE_FLAGS.DOMAINS,
                    },
                    {
                      label: "About",
                      link: routes.site.visibilityEngine.about({ id }),
                      feature: FEATURE_FLAGS.DOMAINS,
                    },
                  ]
                : []),
              ...(aiVisibilityEngineAvailable
                ? [
                    {
                      label: "Traffic",
                      link: routes.site.trafficMetrics({ id }),
                      feature: FEATURE_FLAGS.SITES_VISIBILITY_ENGINE,
                    },
                  ]
                : []),
            ],
          }),
          ...(liveEditorEnabled
            ? [
                createMenuItem({
                  label: "Live Editor",
                  iconName: "LiveEditorIcon",
                  subMenu: [
                    {
                      label: "Install",
                      link: routes.site.liveEditor.install({ id }),
                      feature: FEATURE_FLAGS.SITES_ENHANCEMENTS_INSTALL,
                    },
                    {
                      label: "Customizations",
                      link: routes.site.liveEditor.customizations({ id }),
                      feature: FEATURE_FLAGS.SITES_ENHANCEMENTS,
                    },
                  ],
                }),
              ]
            : []),

          createMenuItem({
            label: "Links",
            iconName: "LinksIcon",
            subMenu: [
              {
                label: "Authority",
                link: routes.site.links.niche({ id }),
                feature: FEATURE_FLAGS.SITES_LINKS_NICHE,
              },
              {
                label: "Community",
                link: routes.site.links.outreach({ id }),
                feature: FEATURE_FLAGS.SITES_LINKS_OUTREACH,
              },
              {
                label: "Foundation",
                link: routes.site.links.brand({ id }),
                feature: FEATURE_FLAGS.SITES_LINKS_BRAND,
              },
            ],
          }),
          createMenuItem({
            label: "Keywords",
            iconName: "KeywordsIcon",
            subMenu: [
              {
                label: "Keywords",
                link: routes.site.keywords.list({ id }),
                feature: FEATURE_FLAGS.SITES_KEYWORDS,
              },
              {
                label: "Keyword Imports",
                link: routes.site.keywords.imports({ id }),
                feature: FEATURE_FLAGS.SITES_KEYWORD_IMPORTS,
              },
            ],
          }),
          createMenuItem({
            label: "Reports",
            iconName: "ReportsIcon",
            subMenu: [
              {
                label: "Built Links",
                link: routes.site.reports.builtLinks({ id }),
                feature: FEATURE_FLAGS.SITES_REPORTS_BUILT_LINKS,
              },
              {
                label: "Keyword Rankings",
                link: routes.site.reports.keywordRanking({ id }),
                feature: FEATURE_FLAGS.SITES_REPORTS_KEYWORD_RANKINGS,
              },
              {
                label: "Site Sales",
                link: routes.site.reports.siteSales({ id }),
                feature: FEATURE_FLAGS.SITES_REPORTS_SITE_SALES,
              },
              {
                label: "Recommendation Exports",
                link: routes.site.reports.recommendationExports({ id }),
                feature: FEATURE_FLAGS.SITES_REPORTS_RECOMMENDATION_EXPORTS,
              },
              {
                label: "Work Done",
                link: routes.site.reports.workDone({ id }),
                feature: FEATURE_FLAGS.SITES_REPORTS_WORK_DONE,
              },
              {
                label: "Client Reports",
                link: routes.site.reports.client({ id }),
                feature: FEATURE_FLAGS.SITES_REPORTS_CLIENT,
              },
            ],
          }),
        ],
      },
      {
        menuTitle: "Automations",
        menuItems: [
          createMenuItem({
            label: "OnPage",
            link: routes.site.onpage({ id }),
            iconName: "OnPageIcon",
            feature: FEATURE_FLAGS.SITES_RECOMMENDATIONS,
          }),

          ...(contentAvailable
            ? [
                createMenuItem({
                  label: "Content",
                  link: routes.site.content({ id }),
                  iconName: "ContentIcon",
                  feature: FEATURE_FLAGS.SITES_CUSTOMIZATIONS,
                }),
              ]
            : []),

          ...(schemaAvailable
            ? [
                createMenuItem({
                  label: "Schema",
                  link: routes.site.schema({ id }),
                  iconName: "SchemaIcon",
                  feature: FEATURE_FLAGS.SITES_SCHEMA,
                }),
              ]
            : []),

          // ai.md is generated for every site (preview for non-engine teams),
          // so it isn't gated on the AI Visibility Engine — only on the flag.
          createMenuItem({
            label: "AI Instructions",
            link: routes.site.aiInstructions({ id }),
            iconName: "DescriptionIcon",
            feature: FEATURE_FLAGS.SITES_AI_INSTRUCTIONS,
          }),

          createMenuItem({
            label: "Interlinks",
            link: routes.site.interlinks({ id }),
            iconName: "InterlinksIcon",
            feature: FEATURE_FLAGS.SITES_INTERLINKS,
          }),
        ],
      },
    ];

    return filterMenuGroupsByFeatureFlags(dashboardMenu, flags);
  }, [
    id,
    crawlStatus,
    shouldShowGoogleDash,
    flags,
    liveEditorEnabled,
    contentAvailable,
    schemaAvailable,
    aiVisibilityEngineAvailable,
    shouldShowAiVisibilityEngine,
    site?.delegated_domain?.id,
  ]);

  const siteFavicon =
    typeof site?.favicon === "object" ? (
      <img
        src={site?.favicon?.url}
        alt="Site favicon"
        className={style.sidebarTitleFavicon}
      />
    ) : (
      <Icon
        iconName="SiteFaviconPlaceholder"
        width="1rem"
        height="1rem"
        className={style.sidebarTitleFavicon}
      />
    );

  return (
    <div
      className={clsx(style.sidebar, { [style.sidebarClosed]: !isSidebarOpen })}
    >
      <div className={style.sidebarContent}>
        <div
          className={clsx(style.sidebarTitle, {
            [style.sidebarTitleClosed]: !isSidebarOpen,
          })}
        >
          <Button
            noHover
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className={
              !isSidebarOpen
                ? style.sidebarTitleButtonClosed
                : style.sidebarTitleButton
            }
          >
            {!isSidebarOpen && siteFavicon}
            <Icon
              iconName={isSidebarOpen ? "ArrowLeftIcon" : "ArrowRightIcon"}
              width="1rem"
              height="1rem"
              className={style.sidebarTitleIcon}
            />

            <span className={style.sidebarTitleTextWrapper}>
              {isSidebarOpen && siteFavicon}
              {isSidebarOpen && (
                <span
                  className={clsx(style.sidebarTitleText, {
                    [style.sidebarTitleClosed]: !isSidebarOpen,
                  })}
                >
                  {isLoadingSite && !site?.fqdn ? (
                    <Skeleton width={120} height={16} />
                  ) : (
                    site?.fqdn
                  )}
                </span>
              )}
            </span>
          </Button>
        </div>
        <div
          className={clsx(style.mainMenuDashboard, {
            [style.mainMenuClosed]: !isSidebarOpen,
          })}
        >
          {!isWhiteLabel() && (
            <>
              <div
                role="button"
                className={style.sidebarGoalsButton}
                onClick={(event) => {
                  if (event.currentTarget === popoverAnchorEl) {
                    handleClosePopover();
                  } else {
                    handleOpenPopover(event);
                  }
                }}
              >
                <span className={style.sidebarGoalsButtonTitleIcon}>
                  <Icon iconName="StarIcon" width="1.5rem" height="1.2rem" />
                </span>
                {isSidebarOpen && (
                  <span
                    className={clsx(style.sidebarGoalsButtonTitle, {
                      // @ts-expect-error TS2551 non-existing className
                      [style.sidebarGolasButtonClosed]: !isSidebarOpen,
                    })}
                  >
                    Get Started ({siteGoals?.goalsLabel})
                  </span>
                )}
              </div>
              <Popper
                open={Boolean(popoverAnchorEl)}
                anchorEl={popoverAnchorEl}
                placement="bottom-start"
                style={{
                  zIndex: 300,
                }}
              >
                <ClickAwayListener onClickAway={handleClosePopover}>
                  <div className={style.popperWrapper}>
                    {site && (
                      <Optimization
                        site={site}
                        goalsLabel={siteGoals?.goalsLabel ?? ""}
                        siteGoals={siteGoals?.filteredGoals ?? {}}
                        isSiteGoalsLoading={isSiteGoalsLoading}
                        onLinkClick={handleClosePopover}
                      />
                    )}
                  </div>
                </ClickAwayListener>
              </Popper>
            </>
          )}
          <Menu
            groups={filteredDashboardMenu}
            isSidebarOpen={isSidebarOpen}
            activePath={activePath}
          />

          {isSidebarEmpty && (
            <div className={clsx(style.footer, style.footerWithMargin)}>
              <ThemeToggleCompact
                isSidebarOpen={isSidebarOpen}
                className={style.menuLink}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardSidebar;
